import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Acorn, ArrowRight, Bird, Bug, Butterfly, Camera, Drop, Fish, Leaf, PawPrint,
  SealCheck, ShieldWarning, Virus,
} from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import GuidePdfDownload from "@/components/GuidePdfDownload";
import GuideListenButton, { guideToSections } from "@/components/GuideListenButton";
import { stampGuide } from "@/pages/NaturePassport";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const GROUP_ICONS = { mammals: PawPrint, birds: Bird, insects: Butterfly, reptiles: Bug, amphibians: Drop, "fish-marine": Fish, fungi: Acorn, other: Virus };

export default function SpeciesGroupPage() {
  const { group } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    stampGuide(`species:group:${group}`);
    http.get(`/speciespulse/groups/${group}`).then(({ data }) => setData(data)).catch(() => setMissing(true));
  }, [group]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const g = data?.group;
  const Icon = GROUP_ICONS[group] || Bug;

  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="speciespulse-group">
      {g && (
        <SeoMeta
          title={`${g.label} — SpeciesPulse`}
          description={g.tagline}
          canonical={`${origin}/speciespulse/group/${group}`}
          jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: `${g.label} — SpeciesPulse`, url: `${origin}/speciespulse/group/${group}` }}
        />
      )}
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "SpeciesPulse", to: "/speciespulse" }, { label: g?.label || group }]} />

        {missing ? (
          <div className="mt-16 text-center" data-testid="speciespulse-group-missing">
            <h1 className="font-serif-display text-3xl text-[#14532D]">Unknown group</h1>
            <p className="mt-2 text-sm text-[#4B5563]">This SpeciesPulse group doesn't exist yet.</p>
            <Link to="/speciespulse" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white">
              Back to SpeciesPulse <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        ) : !g ? (
          <div className="mt-16 text-center text-sm text-[#6B7280]">Loading…</div>
        ) : (
          <>
            {/* Header */}
            <div className="mt-6 flex flex-wrap items-center gap-5">
              {data.art ? (
                <img src={abs(data.art)} alt={g.label} className="h-24 w-24 rounded-full object-cover border-2 border-[#166534]/15 shadow" />
              ) : (
                <span className="flex h-24 w-24 items-center justify-center rounded-full bg-[#F0FDF4] border-2 border-[#166534]/15">
                  <Icon size={40} weight="fill" className="text-[#166534]" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212]">SpeciesPulse group</div>
                <h1 className="font-serif-display text-4xl md:text-5xl text-[#166534]" data-testid="speciespulse-group-title">{g.label}</h1>
                <p className="mt-1 max-w-2xl text-sm md:text-base text-[#374151]">{g.tagline}</p>
              </div>
              <Link to="/identify" data-testid="speciespulse-group-identify"
                className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
                <Camera size={13} weight="bold" /> Identify one
              </Link>
              <GuidePdfDownload url={`${API_BASE}/api/speciespulse/groups/${group}/field-guide.pdf`}
                label="Kids field guide" testid="speciespulse-group-field-guide" />
              <Link to={`/read/articles/species-button-${group}`} data-testid="speciespulse-group-longread"
                className="inline-flex items-center gap-2 rounded-full border border-[#B85F2A]/30 bg-[#FFFBEB] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#B85F2A] hover:bg-[#FEF3C7] transition-colors">
                Featured long read
              </Link>
            </div>
            <ListenTranslateBar text={`${g.label}. ${g.tagline}`} testidPrefix="speciespulse-group-tools" />
            {data.content && (
              <div className="mt-3">
                <GuideListenButton sections={guideToSections(data.content)} testid="speciespulse-guide-listen" />
              </div>
            )}

            {/* Safety banner (fungi / reptiles) — prominent, above the AI guide */}
            {g.safety_note && (
              <div className="mt-5 rounded-2xl border border-[#B45309]/30 bg-[#FFFBEB] p-4 flex gap-3" data-testid="speciespulse-group-safety">
                <ShieldWarning size={20} weight="fill" className="shrink-0 mt-0.5 text-[#B45309]" />
                <p className="text-sm text-[#78350F] leading-relaxed">{g.safety_note}</p>
              </div>
            )}

            {/* AI-authored field guide + attributed photo */}
            {data.content && (
              <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_1fr]" data-testid="speciespulse-group-guide">
                <div className="space-y-5">
                  {data.content.intro && (
                    <p className="text-[15px] text-[#374151] leading-relaxed whitespace-pre-line" data-testid="speciespulse-guide-intro">{data.content.intro}</p>
                  )}
                  {data.content.identification_tips?.length > 0 && (
                    <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm" data-testid="speciespulse-guide-tips">
                      <h2 className="font-serif-display text-xl text-[#14532D]">Field identification tips</h2>
                      <ul className="mt-2 space-y-1.5">
                        {data.content.identification_tips.map((t, i) => (
                          <li key={i} className="flex gap-2 text-sm text-[#374151]">
                            <Leaf size={13} weight="fill" className="mt-1 shrink-0 text-[#65A30D]" /> {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {data.content.did_you_know?.length > 0 && (
                      <div className="rounded-2xl bg-[#F0FDF4] border border-[#166534]/10 p-5" data-testid="speciespulse-guide-facts">
                        <h2 className="font-serif-display text-xl text-[#14532D]">Did you know</h2>
                        <ul className="mt-2 space-y-1.5">
                          {data.content.did_you_know.map((f, i) => (
                            <li key={i} className="text-sm text-[#374151] leading-snug">· {f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {data.content.beginner_steps?.length > 0 && (
                      <div className="rounded-2xl bg-[#F0FDF4] border border-[#166534]/10 p-5" data-testid="speciespulse-guide-steps">
                        <h2 className="font-serif-display text-xl text-[#14532D]">First-time observer</h2>
                        <ol className="mt-2 space-y-1.5">
                          {data.content.beginner_steps.map((s, i) => (
                            <li key={i} className="flex gap-2 text-sm text-[#374151] leading-snug">
                              <span className="font-mono-tech text-[10px] text-[#166534] mt-0.5">{i + 1}.</span> {s}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                  {data.content.conservation_note && (
                    <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm" data-testid="speciespulse-guide-conservation">
                      <h2 className="font-serif-display text-xl text-[#14532D]">Conservation</h2>
                      <p className="mt-1.5 text-sm text-[#374151] leading-relaxed">{data.content.conservation_note}</p>
                    </div>
                  )}
                </div>
                {data.photo ? (
                  <figure className="rounded-3xl overflow-hidden border border-black/5 shadow-sm self-start" data-testid="speciespulse-guide-photo">
                    <img src={abs(data.photo.url)} alt={data.photo.alt} className="w-full object-cover" loading="lazy" />
                    <figcaption className="px-4 py-2.5 bg-white text-xs text-[#6B7280]">
                      {data.photo.ai_generated ? (
                        <span data-testid="speciespulse-photo-ai">NaturePulse AI illustration — a real photograph with photographer attribution joins this page when available.</span>
                      ) : (
                        <>
                          Photo by{" "}
                          <a href={data.photo.photographer_link} target="_blank" rel="noreferrer" className="text-[#166534] underline underline-offset-2" data-testid="speciespulse-photo-photographer">
                            {data.photo.photographer_name}
                          </a>{" "}
                          on{" "}
                          <a href={data.photo.unsplash_link} target="_blank" rel="noreferrer" className="text-[#166534] underline underline-offset-2" data-testid="speciespulse-photo-unsplash">
                            Unsplash
                          </a>
                        </>
                      )}
                    </figcaption>
                  </figure>
                ) : data.art ? (
                  <figure className="rounded-3xl overflow-hidden border border-black/5 shadow-sm self-start" data-testid="speciespulse-guide-photo">
                    <img src={abs(data.art)} alt={g.label} className="w-full object-cover" loading="lazy" />
                    <figcaption className="px-4 py-2.5 bg-white text-xs text-[#6B7280]">
                      NaturePulse illustration — a real Unsplash photograph with photographer attribution joins this page shortly.
                    </figcaption>
                  </figure>
                ) : null}
              </div>
            )}

            {/* Subcategories */}
            <div className="mt-8" data-testid="speciespulse-group-subcats">
              <h2 className="font-serif-display text-2xl text-[#14532D]">Browse {g.label.toLowerCase()}</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {(g.subcategories || []).map((s) => (
                  <Link key={s} to={`/search?q=${encodeURIComponent(s.toLowerCase())}`}
                    data-testid={`speciespulse-subcat-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="rounded-full border border-[#166534]/20 bg-white px-4 py-2 text-sm text-[#14532D] hover:bg-[#166534] hover:text-white transition-colors">
                    {s}
                  </Link>
                ))}
              </div>
            </div>

            {/* Species grid */}
            <div className="mt-10" data-testid="speciespulse-group-species">
              <div className="flex items-center justify-between">
                <h2 className="font-serif-display text-2xl text-[#14532D]">Documented species</h2>
                <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">
                  {data.species?.length || 0} profile{(data.species?.length || 0) === 1 ? "" : "s"}
                </span>
              </div>
              {data.species?.length > 0 ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {data.species.map((s) => (
                    <Link key={s.slug} to={`/speciespulse/species/${s.slug}`} data-testid={`speciespulse-species-${s.slug}`}
                      className="group rounded-2xl border border-black/5 bg-white p-5 shadow-sm hover:border-[#166534]/40 hover:shadow-md transition-all">
                      <div className="font-semibold text-[#14532D] group-hover:text-[#166534]">{s.title}</div>
                      {s.scientific_name && <div className="text-sm italic text-[#6B7280]">{s.scientific_name}</div>}
                      {s.card_fact && <p className="mt-2 text-[13px] text-[#4B5563] leading-snug line-clamp-2">{s.card_fact}</p>}
                      <span className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#166534]">
                        Open profile <ArrowRight size={10} weight="bold" />
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="mt-4 rounded-2xl border border-dashed border-[#166534]/25 bg-[#F0FDF4]/50 p-5 text-sm text-[#4B5563]">
                  No curated {g.label.toLowerCase()} profiles yet — but the identification engine already
                  knows this group. Photograph one and your observation can start the record.
                </p>
              )}
            </div>

            {/* Observations */}
            {data.observations?.length > 0 && (
              <div className="mt-10" data-testid="speciespulse-group-observations">
                <h2 className="font-serif-display text-2xl text-[#14532D]">Recent observations</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {data.observations.map((o) => (
                    <Link key={o.id} to={`/observations/${o.id}`} data-testid={`speciespulse-group-obs-${o.id}`}
                      className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-sm hover:border-[#166534]/40 transition-colors">
                      {o.media?.[0]?.url && <img src={abs(o.media[0].url)} alt={o.title} className="h-28 w-full object-cover" loading="lazy" />}
                      <div className="p-3">
                        <div className="font-medium text-sm text-[#14532D] leading-tight">{o.title || "Observation"}</div>
                        <div className="mt-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#6B7280]">{o.date}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Trust note */}
            <div className="mt-10 rounded-2xl bg-[#F0FDF4] border border-[#166534]/10 p-5 flex flex-wrap items-center gap-3" data-testid="speciespulse-group-trust">
              <SealCheck size={18} weight="fill" className="text-[#166534]" />
              <p className="flex-1 min-w-[240px] text-sm text-[#374151]">
                Profiles are curated from authoritative sources and community observations. AI suggestions are
                always labeled as candidates — the trust ladder shows how far each identification has been verified.
              </p>
              <Link to="/speciespulse" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#166534] hover:underline">
                All groups <ArrowRight size={11} weight="bold" />
              </Link>
            </div>
            <div className="h-10" />
          </>
        )}
      </div>
    </main>
  );
}
